import { useAuth } from '@teko/auth-react';
import { logger } from '@teko/log';
import { PrivacyPolicy, SubmittedPrivacyPolicy } from '@teko/privacy-policy';
import { isEmptyObject } from '@teko/utils';
import {
  COOKIE_CONSENT_DURATION,
  SUBMIT_COOKIE_CONSENT_PREFIX,
} from 'constants/cookieConsent';
import { useServices } from 'contexts';
import {
  convertDateToUnixTimeStamp,
  getExpireTime,
  hasCookieByName,
  isServerless,
  setCookieByName,
} from 'helpers';
import { useCallback, useEffect, useState } from 'react';
import { useCookieConsentStore } from 'stores';
import { Consent, PolicyFilter, PolicyTypeId } from 'types';

export const useCookiesConsent = () => {
  const { user } = useAuth();
  const { privacyPolicyService } = useServices();
  const visible = useCookieConsentStore((state) => state.visible);
  const setVisible = useCookieConsentStore((state) => state.setVisible);
  const cookieConsent = useCookieConsentStore((state) => state.cookieConsent);
  const setCookieConsent = useCookieConsentStore(
    (state) => state.setCookieConsent,
  );
  const submitConsentToServices = useCookieConsentStore(
    (state) => state.submitConsentToServices,
  );
  const [policies, setPolicies] = useState<PrivacyPolicy[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Submit user consent to UPP. Sets the submit marker cookie on success.
  // Leaves no side-effects on failure so callers can retry.
  const submitToUPP = useCallback(
    async (consents: Consent[]) => {
      if (!user?.userId || !isServerless()) return;
      setIsSubmitting(true);
      try {
        const expiredAt = getExpireTime(COOKIE_CONSENT_DURATION);
        const submitPolicies: SubmittedPrivacyPolicy[] = consents.map((c) => ({
          id: c.id,
          version: c.version,
          language: c.language,
          isApproved: c.isApproved,
        }));
        await privacyPolicyService.submitPolicies(
          submitPolicies,
          convertDateToUnixTimeStamp(expiredAt),
        );
        setCookieByName(
          `${SUBMIT_COOKIE_CONSENT_PREFIX}${user.userId}`,
          'true',
          expiredAt,
        );
      } catch (e) {
        logger.error(e, 'Submit cookie consent to UPP error');
      } finally {
        setIsSubmitting(false);
      }
    },
    [privacyPolicyService, user?.userId],
  );

  const onToggle = (id: number) => {
    setPolicies((prev) =>
      prev.map((p) => (p.id === id ? { ...p, isApproved: !p.isApproved } : p)),
    );
  };

  const buildCookiePolicies = (overrideAll?: boolean): Consent[] =>
    policies
      .filter((p) => p.policyTypeId === PolicyTypeId.TYPE_5)
      .map((p) => ({
        id: p.id,
        version: p.version,
        language: p.language,
        isApproved: overrideAll ?? p.isApproved ?? false,
        policyValues: p.policyValues ?? [],
      }));

  const onAccept = async (overrideAll?: boolean) => {
    const consents = buildCookiePolicies(overrideAll);
    // Save to browser cookie
    setCookieConsent(consents);
    // Submit user's consent to GA/GTM, Clarity, FB pixel,...
    submitConsentToServices(consents);
    // Submit to UPP (shows loading state while in progress).
    // Modal stays open until the first attempt resolves, success or failure.
    await submitToUPP(consents);
    setVisible(false);
  };

  useEffect(() => {
    if (!isEmptyObject(cookieConsent)) return;

    const fetchPolicies = async () => {
      try {
        const data = await privacyPolicyService.getPolicies(
          [PolicyFilter.ALL_POLICIES],
          [PolicyTypeId.TYPE_4, PolicyTypeId.TYPE_5],
        );
        setPolicies(data);
        if (!!data.length) {
          setVisible(true);
        }
      } catch (e) {
        logger.error(e, 'Fetch cookie consent policies error');
      }
    };
    fetchPolicies();
  }, [privacyPolicyService, cookieConsent]);

  // Silent retry: if the user already has a consent cookie but the UPP submit
  // marker is missing (e.g. previous attempt failed), resubmit once on mount.
  useEffect(() => {
    if (isEmptyObject(cookieConsent) || !user?.userId || !isServerless())
      return;
    const isConsentSubmitted = hasCookieByName(
      `${SUBMIT_COOKIE_CONSENT_PREFIX}${user.userId}`,
    );
    if (!isConsentSubmitted) {
      submitToUPP(cookieConsent.consents);
    }
    // Only call silent submit one time if this user's consent has not been submitted before when first load page
    // disable exhaustive-deps on purpose
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, submitToUPP]);

  const firstViewDescriptions = policies.filter(
    (p) => p.policyTypeId === PolicyTypeId.TYPE_4,
  );
  const toggleablePolicies = policies.filter(
    (p) => p.policyTypeId === PolicyTypeId.TYPE_5,
  );

  return {
    visible,
    isSubmitting,
    firstViewDescriptions,
    toggleablePolicies,
    onToggle,
    onAccept,
  };
};
